import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faDownload, faFileUpload } from '@fortawesome/free-solid-svg-icons';
import { isImageExtension } from '../../utils/isImageExtension';
import { BaseComponent } from '../base/base.component';
import { downloadBlobFile } from '../../utils/downloadBlobFile';
import { downloadFileByUrl } from '../../utils/downloadFileByUrl';

@Component({
  selector: 'app-document-input',
  templateUrl: './document-input.component.html',
  styleUrls: ['./document-input.component.scss'],
})
export class DocumentInputComponent
  extends BaseComponent
  implements AfterViewInit
{
  @ViewChild('docPreview') docPreview!: ElementRef;
  @ViewChild('inputFile') inputFile!: ElementRef;

  @Input() descricao!: string;

  @Input() sent!: boolean;
  @Input() showCloseButton: 'always' | 'hasFile' | 'never' = 'hasFile';
  @Input() required = false;

  @Output() changed = new EventEmitter<File>();
  @Output() removed = new EventEmitter<File>();

  touched = false;

  faFileUpload = faFileUpload as IconProp;
  faDownload = faDownload;

  handlerHasValue = false;

  privateFile: File | null = null;

  private privateUrl!: string;

  get shouldShowInsertedBorder(): boolean {
    return this.hasValue;
  }

  get hideCloseButton(): boolean {
    if (this.showCloseButton === 'always') {
      return false;
    }

    if (this.showCloseButton === 'hasFile') {
      return !this.hasValue;
    }

    return true;
  }

  get fileExtension(): string | null {
    if (this.privateUrl) {
      return this.privateUrl.split('.')?.pop()?.toLowerCase() ?? null;
    }

    if (this.privateFile) {
      return this.privateFile.name.split('.')?.pop()?.toLowerCase() ?? null;
    }

    return null;
  }

  get isNonImage(): boolean {
    return !isImageExtension(this.fileExtension ?? '');
  }

  get hasValue(): boolean {
    return this.handlerHasValue;
  }

  @Input() set hasValue(value: boolean) {
    if (!value && this.inputFile) {
      const inputEl = this.inputFile.nativeElement as any;
      const imgEl = this.docPreview.nativeElement as any;

      imgEl.removeAttribute('src');
      inputEl.value = null;
    }

    this.handlerHasValue = value;
  }

  @Input() set url(url: string) {
    this.privateUrl = url;
    if (url) {
      this.showImgPreview(url);
    }
  }

  @Input() set file(file: File | null) {
    this.privateFile = file;
    if (this.inputFile && file) {
      this.showImgPreview(file);
    }
  }

  ngAfterViewInit(): void {
    if (this.privateUrl) {
      this.showImgPreview(this.privateUrl);
    }
  }

  onInput(event: Event): void {
    const file = this.extractFileFromEvent(event);

    if (!file) {
      return;
    }

    this.changed.emit(file);
    this.touched = true;
  }

  remove(event: Event): void {
    event.stopImmediatePropagation();

    this.removed.emit();
  }

  download(event: Event): void {
    event.stopImmediatePropagation();

    if (this.privateUrl) {
      downloadFileByUrl(this.privateUrl);
      return;
    }

    const fileName = this.file?.name ?? '';

    downloadBlobFile(this.file as Blob, fileName);
  }

  onContainerClick(): void {
    if (this.shouldShowInsertedBorder) {
      return;
    }

    this.inputFile.nativeElement.click();
  }

  private extractFileFromEvent(event: Event): File | null {
    const target = event?.target as any;

    return target?.files?.[0] as File;
  }

  private showImgPreview(urlOrFile: string | File): void {
    const imgEl = this.docPreview?.nativeElement as any;

    if (!imgEl) {
      return;
    }

    if (typeof urlOrFile === 'string') {
      imgEl.src = urlOrFile;
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(urlOrFile);
    reader.onload = () => {
      imgEl.src = reader.result;
    };
  }
}
